import React, { useEffect, useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';

import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import CheckoutForm from './CheckoutForm';

import { UserDataContext } from '../contexts';

import useFetch from '../hooks/useFetch';
import { BASE_SUBSCRIPTION_PRICE, getTenantFromUrl } from '../utils';

const initioStripePromise = loadStripe(process.env.REACT_APP_STRIPE_LIVE_KEY);
const redwoodseedStripePromise = loadStripe(process.env.REACT_APP_STRIPE_REDWOODSEED_LIVE_KEY);

const tenant = getTenantFromUrl();

const stripePromise = tenant === 'redwoodseed' ? redwoodseedStripePromise : initioStripePromise;

export default function Payment({
  makePaymentButtonRef,
  setPaymentFormIsLoading,
  setPaymentIsProcessing,
  setPaymentIsCompleted,
  discountPaymentData,
  setDiscountPaymentData,
  transactionId,
}) {
  const { userData } = useContext(UserDataContext);

  const [clientSecret, setClientSecret] = useState('');
  const initialPaymentCreated = useRef(false);
  const lastAmount = useRef(null);

  const [, paymentIntentRequest] = useFetch();

  function loadClientSecret() {
    // Use discounted amount if available, otherwise use base price
    const amount = discountPaymentData.validCode && discountPaymentData.chargeAmount 
      ? discountPaymentData.chargeAmount 
      : BASE_SUBSCRIPTION_PRICE; // Always use our constant instead of env var
    
    // Set the initial amount
    lastAmount.current = amount;

    paymentIntentRequest({
      url: '/stripe/create-payment-intent',
      body: {
        currency: 'usd',
        amount,
        paymentMethodType: ['card'],
        metadata: {
          accountId: userData.userAttributes.userId,
          companyId: userData.userAttributes.companyId,
          companyName: userData.userAttributes.companyName,
          code_applied: discountPaymentData.validCode || false,
          discount_percentage: discountPaymentData.discountPercentage || 0,
          original_amount: discountPaymentData.originalAmount || BASE_SUBSCRIPTION_PRICE,
          discount_code: discountPaymentData.code || null,
          ...transactionId && { transactionId },
        },
      },
      onSuccess: (response) => {
        setClientSecret(JSON.parse(response).clientSecret);
        setPaymentFormIsLoading(false);
      },
    });
  }

  // Only create initial payment intent once
  useEffect(() => {
    if (!initialPaymentCreated.current) {
      loadClientSecret();
      initialPaymentCreated.current = true;
    }
  }, []);

  // Store the current amount for payment processing (don't create new payment intent yet)
  useEffect(() => {
    if (initialPaymentCreated.current) {
      // Determine the current amount
      const currentAmount = discountPaymentData.validCode && discountPaymentData.chargeAmount 
        ? discountPaymentData.chargeAmount 
        : BASE_SUBSCRIPTION_PRICE;
      
      // Update the last amount without creating a new payment intent
      lastAmount.current = currentAmount;
    }
  }, [discountPaymentData]);

  if (clientSecret.length > 0) {
    return (
      <Elements
        key={clientSecret}
        stripe={stripePromise}
        options={{ clientSecret }}
      >
        <CheckoutForm
          makePaymentButtonRef={makePaymentButtonRef}
          setPaymentIsProcessing={setPaymentIsProcessing}
          setPaymentIsCompleted={setPaymentIsCompleted}
          clientSecret={clientSecret}
          discountPaymentData={discountPaymentData}
          setDiscountPaymentData={setDiscountPaymentData}
        />
      </Elements>
    );
  }

  return <div className="loading-payment-form"><div className="dots-circle-spinner" /></div>;
}

Payment.propTypes = {
  makePaymentButtonRef: PropTypes.object.isRequired,
  setPaymentIsProcessing: PropTypes.func.isRequired,
  setPaymentIsCompleted: PropTypes.func.isRequired,
  setPaymentFormIsLoading: PropTypes.func.isRequired,
  discountPaymentData: PropTypes.object.isRequired,
  setDiscountPaymentData: PropTypes.func.isRequired,
  transactionId: PropTypes.string,
};
